import { ArrowRight, Plus } from 'lucide-react';
import { T } from '../../i18n/copy';
import Reveal from '../Reveal';
import SectionHead from '../SectionHead';

export default function HowWeWork({ lang }) {
  const t = T.how;
  return (
    <section id="how-we-work" data-header-theme="midnight" data-testid="how-we-work-section" className="bg-midnight pb-10 text-bone md:pb-12">
      <div className="editorial-margins pt-10 md:pt-12">
        <SectionHead tone="dark" eyebrow={t.eyebrow[lang]} index={t.index} />
        <div className="mt-6 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="whitespace-pre-line font-tight font-semibold text-[clamp(1.75rem,2.6vw,2.4rem)] leading-[1.08] tracking-[-0.02em] text-bone">
              {t.headline[lang]}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:justify-self-end">
            <p className="max-w-sm text-base leading-snug text-steel-light">{t.intro[lang]}</p>
          </Reveal>
        </div>

        <div className="mt-8 hidden gap-x-8 gap-y-10 sm:grid sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-12" data-testid="how-steps">
          {t.steps[lang].map((step, i) => (
            <Reveal key={step.num} delay={i * 0.06} testid={`how-step-${i}`} className="h-full lg:pr-2">
              <div className="flex h-full flex-col border-t border-bone/15 pt-5">
                <div className="flex items-baseline gap-2">
                  <span className="font-tight text-3xl font-medium leading-none text-vermilion" data-testid={`how-step-num-${i}`}>{step.num}</span>
                  <h3 className="font-tight text-lg font-semibold text-bone" data-testid={`how-step-title-${i}`}>{step.title}</h3>
                </div>
                <p className="mt-3.5 text-base leading-snug text-bone/70">{step.lead}</p>
                {step.body && <p className="mt-2.5 text-base leading-snug text-bone/70">{step.body}</p>}
                {!step.body && step.listLabel === 'examines' && <p className="eyebrow mt-4 text-bone/40">{t.examines[lang]}</p>}
                {!step.body && step.listLabel === 'compare' && <p className="eyebrow mt-4 text-bone/40">{t.compare[lang]}</p>}
                {step.list.length > 0 && (
                  <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
                    {step.list.map((li) => (
                      <li key={li} className="text-[14px] leading-relaxed text-bone/70">{li}</li>
                    ))}
                  </ul>
                )}
                {step.num === '04' && <p className="mt-3 text-base leading-snug text-bone/70">{t.decide[lang]}</p>}
                <div className="mt-auto flex flex-wrap items-center gap-x-2.5 gap-y-1.5 pt-5" data-testid={`how-step-micro-${i}`}>
                  {step.micro.map((m, j) => (
                    <span key={m} className="flex items-center gap-2">
                      <span className="eyebrow text-steel-light">{m}</span>
                      {j < step.micro.length - 1 && <ArrowRight className="h-3 w-3 text-vermilion" strokeWidth={1.5} />}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-8 sm:hidden" data-testid="how-steps-mobile">
          {t.steps[lang].map((step, i) => (
            <details key={step.num} data-testid={`how-step-mobile-${i}`} className="group border-t border-bone/15">
              <summary className="flex cursor-pointer list-none items-center gap-3 py-4">
                <span className="font-tight text-xl font-medium text-vermilion">{step.num}</span>
                <span className="flex-1 font-tight text-lg font-semibold text-bone">{step.title}</span>
                <Plus className="h-4 w-4 shrink-0 text-bone/50 transition-transform duration-200 group-open:rotate-45" strokeWidth={1.5} />
              </summary>
              <div className="pb-5">
                <p className="text-base leading-snug text-bone/70">{step.lead}</p>
                {step.body && <p className="mt-2 text-base leading-snug text-bone/70">{step.body}</p>}
                {!step.body && step.listLabel === 'examines' && <p className="eyebrow mt-3 text-bone/40">{t.examines[lang]}</p>}
                {!step.body && step.listLabel === 'compare' && <p className="eyebrow mt-3 text-bone/40">{t.compare[lang]}</p>}
                {step.list.length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {step.list.map((li) => (
                      <li key={li} className="text-[14px] text-bone/70">{li}</li>
                    ))}
                  </ul>
                )}
                {step.num === '04' && <p className="mt-2 text-base leading-snug text-bone/70">{t.decide[lang]}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-bone/15 pt-3">
                  {step.micro.map((m, j) => (
                    <span key={m} className="flex items-center gap-2">
                      <span className="eyebrow text-steel-light">{m}</span>
                      {j < step.micro.length - 1 && <ArrowRight className="h-3 w-3 text-vermilion" strokeWidth={1.5} />}
                    </span>
                  ))}
                </div>
              </div>
            </details>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-bone/12 pt-6" data-testid="how-closing">
          {t.closing[lang].map((w, i) => (
            <span key={w} className="flex items-center gap-3">
              <span className="eyebrow text-bone/70">{w}</span>
              {i < 3 && <ArrowRight className="h-3 w-3 text-vermilion" strokeWidth={1.5} />}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
